/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  type MutableRefObject,
  useEffect,
  useState,
  Fragment,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useBotInfoStore } from '@coze-studio/bot-detail-store/bot-info'; // Keep if botId is needed directly
import {
  PictureUpload,
  type UploadValue,
} from '@coze-common/biz-components/picture-upload';
import { I18n } from '@coze-arch/i18n';
import { Modal, Icon } from '@coze-arch/coze-design';
import { Radio, Popover, UIButton } from '@coze-arch/bot-semi';
import { useSpaceStore } from '@coze-arch/bot-studio-store';
import {
  FileBizType,
  IconType,
  type DraftBot,
} from '@coze-arch/bot-api/developer_api';
import {
  useAgentPersistence,
  useAgentFormManagement,
  AgentInfoForm,
} from '@coze-agent-ide/space-bot/hook';
import { IconCozArrowDown, IconCozScene } from '@coze-arch/coze-design/icons';
import { IconAgentIcon } from '@coze-arch/bot-icons';
import { agentKindList } from '@rokid/const';
import { getLocalAgentUsers } from '@rokid/services';
import { useUserInfo } from '@coze-arch/foundation-sdk';
import './index.less';
export interface CreateAgentEntityProps {
  onBefore?: () => void;
  onError?: () => void;
  /** Return Promise only if you need the onSuccess callback to block the pop-up window from closing automatically. */
  onSuccess?: (
    botId?: string,
    spaceId?: string,
    extra?: {
      botName?: string;
      botAvatar?: string;
      botDesc?: string;
    },
  ) => void | Promise<void>;
  botInfoRef?: MutableRefObject<DraftBot | undefined>;
  mode: 'update' | 'add';
  showSpace?: boolean;
  /**
   * Pass this parameter when you need to control externally which space to create the bot in
   * Only suitable for creating
   */
  spaceId?: string;
  /**
   * Navigation bar
   * Button in the upper right corner of the space workspace
   * */
  bizCreateFrom?: 'navi' | 'space';
}

const getPictureUploadInitValue = (
  botInfo?: Partial<DraftBot>,
): UploadValue | undefined => {
  if (!botInfo?.icon_url) {
    return;
  }
  return [
    {
      url: botInfo.icon_url || '',
      uid: botInfo.icon_uri || '',
    },
  ];
};

export const useCreateOrUpdateAgent = ({
  botInfoRef,
  onBefore,
  onSuccess,
  onError,
  mode,
  showSpace = false, // Not displayed by default
  spaceId: outerSpaceId,
  bizCreateFrom,
}: CreateAgentEntityProps) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const userInfo = useUserInfo();
  //rokid 临时添加的，控制AIUI智能体是否显示
  const [localAgentUsers, setLocalAgentUsers] = useState<string[]>([]);
  const botId = useBotInfoStore(state => state.botId);
  const {
    space: { id: spaceId, hide_operation },
    spaces: { bot_space_list: list },
  } = useSpaceStore();

  const {
    formRef,
    isOkButtonDisable,
    checkErr,
    errMsg,
    confirmDisabled,
    setCheckErr,
    setErrMsg,
    handleFormValuesChange,
    getValues,
    resetFormState,
  } = useAgentFormManagement({ initialBotInfo: botInfoRef?.current });

  const {
    loading: persistenceLoading,
    handleCreateBot,
    handleUpdateBot,
  } = useAgentPersistence({
    mode,
    botId,
    currentSpaceId: spaceId,
    outerSpaceId,
    getValues,
    onSuccess,
    onError,
    onBefore,
    setVisible,
    setCheckErr,
    setErrMsg,
    bizCreateFrom,
    showSpace,
  });

  useEffect(() => {
    if (visible) {
      useSpaceStore
        .getState()
        .fetchSpaces()
        .then(res => {
          if (!formRef.current?.formApi?.getValues()?.spaceId) {
            formRef.current?.formApi?.setValue(
              'spaceId',
              hide_operation
                ? res?.bot_space_list?.[0].id
                : (spaceId ?? res?.bot_space_list?.[0].id),
            );
          }
        });
    }
    if (visible) {
      resetFormState();
    }
  }, [visible]);

  //rokid 临时添加的控制本地智能体是否显示
  useEffect(() => {
    getLocalAgentUsers().then(res => {
      setLocalAgentUsers(res);
    });
  }, []);

  /**
   * @Param _ open source version does not support this parameter
   */
  const startEdit = (status?: boolean) => {
    setVisible(Boolean(status));
  };
  const formInitialValues = botInfoRef?.current || {};
  //rokid 添加，在原有单类型基础上，增加智能体类型选择
  const modalTitle = (
    <h5 className="semi-typography">
      {`${mode === 'add' ? '创建' : '编辑灵珠'}智能体`}
      {mode === 'add' ? (
        <Popover
          key="rizon"
          className={'changeAgentType'}
          trigger="hover"
          position="bottomLeft"
          autoAdjustOverflow={false}
          content={
            <div
              style={{
                background: '#fff',
                padding: '16px',
                border: 'solid 1px #1c1f2314',
              }}
            >
              <Radio.Group
                type="pureCard"
                direction="vertical"
                value={'rizon'}
                defaultValue={'rizon'}
                options={agentKindList
                  .filter(item =>
                    localAgentUsers.includes(userInfo?.user_id_str)
                      ? true
                      : item.key !== 'local',
                  )
                  .map(item => ({
                    icon: (
                      <item.icon
                        size="large"
                        style={{
                          fontSize: '14px',
                          marginRight: '6px',
                          verticalAlign: 'middle',
                        }}
                      />
                    ),
                    name: item.label,
                    key: item.key,
                  }))
                  .map(item => {
                    return {
                      value: item.key,
                      label: (
                        <span>
                          <Icon svg={item.icon} size="large" />
                          {item.name}
                        </span>
                      ),
                      disabled: item.key === 'rizon',
                    };
                  })}
                onChange={e => {
                  if (e.target.value !== 'rizon') {
                    if (location.href.includes('/develop')) {
                      window.changeActiveType(e.target.value);
                    } else {
                      navigate(`/space/${spaceId}/develop`);
                      setTimeout(() => {
                        window.changeActiveType(e.target.value);
                      }, 1000 * 0.5);
                    }
                  }
                }}
              />
            </div>
          }
        >
          <UIButton theme="outline" size="large" style={{ marginLeft: '12px' }}>
            <div
              className={'flex items-center'}
              style={{ color: 'rgba(15, 21, 40, 0.82)' }}
            >
              <IconAgentIcon
                size="large"
                style={{
                  fontSize: '18px',
                  marginRight: '6px',
                  verticalAlign: 'middle',
                }}
              />
              灵珠智能体
              <IconCozArrowDown className="w-4 h-5 coz-fg-secondary ml-10px" />
            </div>
          </UIButton>
        </Popover>
      ) : (
        ''
      )}
    </h5>
  );
  return {
    visible,
    startEdit,
    modal: (
      <Fragment>
        <Modal
          key={bizCreateFrom}
          data-testid="bot.ide.bot_creator.create_bot_modal"
          visible={visible}
          maskClosable={false}
          onCancel={() => {
            setVisible(false);
          }}
          title={modalTitle}
          okText={I18n.t('Confirm')}
          cancelText={I18n.t('Cancel')}
          okButtonProps={{
            disabled: isOkButtonDisable || confirmDisabled,
            loading: persistenceLoading,
          }}
          footer={null}
          keepDOM={false}
          icon={null}
          onOk={() => {
            mode === 'add' ? handleCreateBot() : handleUpdateBot();
          }}
          width={800}
        >
          <AgentInfoForm
            ref={formRef}
            mode={mode}
            showSpace={showSpace}
            initialValues={formInitialValues}
            spacesList={list || []}
            currentSpaceId={outerSpaceId || spaceId}
            hideOperation={hide_operation}
            checkErr={checkErr}
            errMsg={errMsg}
            onValuesChange={handleFormValuesChange}
            slot={
              <PictureUpload
                accept=".jpeg,.jpg,.png,.gif"
                label={I18n.t('bot_edit_profile_pircture')}
                field="bot_uri"
                initValue={getPictureUploadInitValue(formInitialValues)}
                rules={[{ required: true }]}
                fileBizType={FileBizType.BIZ_BOT_ICON}
                iconType={IconType.Bot}
                maxSize={1024 * 5}
                minSize={1}
              />
            }
          />
        </Modal>
      </Fragment>
    ),
  };
};
